import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Grid, Color, Type, Transition } from '../../app/StyleGuide';
import LoadingState from '../../components/LoadingState';
import ErrorMessage from '../../components/ErrorMessage';
import { Container, FixedHeaderContainer, FixedHeaderContent, RaisedContainer } from '../../components/Layout';
import { RoundedIconButton } from '../../components/Buttons';
import { ChurchImage } from '../../components/ChurchImage';
import ActionableTitleBar from '../../components/ActionableTitleBar';
import alertService from '../../services/AlertService';
import windowService from '../../services/windowService';
import { useUser } from '../authentication';
import WhiteLabel from '../../filters/WhiteLabel';
import useQueryParams from '../../hooks/useQueryParams';
import { getUserOrgs } from './api/getUserOrgs';
import { switchOrg } from './api/switchOrg';
import { UserOrg } from './models/userOrg';

type Status = {
  isLoading: boolean;
  isError: boolean;
};

const sortAlphabetically = (orgs: UserOrg[]): UserOrg[] => [...orgs].sort((a, b) => a.name.localeCompare(b.name));

const putOrgAtBeginning = (orgId: string, orgs: UserOrg[]): UserOrg[] => {
  const orgIndex = orgs.findIndex(org => org._id.id === orgId);
  if (orgIndex < 0) return orgs;
  return [orgs[orgIndex], ...orgs.slice(0, orgIndex), ...orgs.slice(orgIndex + 1)];
};

const removeInvalidOrgs = (orgs: UserOrg[]): UserOrg[] => orgs.filter(org => org._id.id);

const ChurchItem = styled(RaisedContainer)`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: ${Grid._4};
  align-items: center;
  padding: ${Grid._4};
  width: 100%;
  border: 2px solid transparent;
  transition: ${Transition.fast};
  &:hover:not(.active) {
    border-color: ${Color.Gray._80};
    box-shadow: none;
    cursor: pointer;
  }
  &.active {
    border-color: ${Color.Gray._80};
    border-top-width: ${Grid._6};
    &:before {
      content: 'Current ${WhiteLabel.labels.org}';
      position: absolute;
      top: -27px;
      width: 100%;
      text-align: center;
      font-size: ${Type.Scale._2};
      text-transform: uppercase;
      text-shadow: ${Type.Shadow};
      color: ${Color.white};
    }
  }
`;

export default function SwitchOrg() {
  const { user } = useUser();
  const currentOrgId = user!.lastSelectedAccount;
  const [status, setStatus] = useState<Status>({ isLoading: true, isError: false });
  const [orgs, setOrgs] = useState<UserOrg[]>([]);
  const { redirect } = useQueryParams();

  const switchChurch = (orgId: string) => {
    if (orgId === currentOrgId) return;

    setStatus({ ...status, isLoading: true });

    switchOrg(user!.userId, orgId)
      .then(() => windowService.redirectTo(redirect || '/dashboard'))
      .catch(reason => {
        console.error(reason);
        alertService.show('An error occurred. Please try again.', 'error');
        setStatus({ ...status, isLoading: false });
      });
  };

  useEffect(() => {
    getUserOrgs()
      .then(data => {
        setOrgs(putOrgAtBeginning(currentOrgId, sortAlphabetically(removeInvalidOrgs(data))));
        setStatus({ ...status, isLoading: false });
      })
      .catch(reason => {
        console.error(reason);
        setStatus({ isLoading: false, isError: true });
      });
  }, []);

  return (
    <FixedHeaderContainer data-qa-hook="switchOrganizationView" fullViewport>
      <ActionableTitleBar
        title="Select A Church"
        subtitle=""
        leftButton={<div />}
        rightButton={
          <RoundedIconButton data-qa-hook="back" onClick={windowService.redirectBack}>
            <i className="fas fa-times fa-fw"></i>
            Close
          </RoundedIconButton>
        }
      />
      <FixedHeaderContent>
        <Container>
          {status.isLoading ? (
            <LoadingState />
          ) : status.isError ? (
            <ErrorMessage>
              A problem occurred showing this page. Please refresh the page to try again.{' '}
              <a href="#/help">Contact Us</a>
            </ErrorMessage>
          ) : (
            <>
              <div
                data-qa-hook="orgCardContainer"
                className="grid-container grid-col-1 grid-sm-col-2 grid-gap-32"
                style={{ margin: `${Grid._10} 0` }}
              >
                {orgs.map((org, i) => (
                  <ChurchItem
                    data-qa-hook="orgCard"
                    key={i}
                    onClick={() => switchChurch(org._id.id)}
                    className={org._id.id === currentOrgId ? 'active' : ''}
                  >
                    <ChurchImage name={org.name} logo={org.logo} />
                    <h4 data-qa-hook="orgName">{org.name}</h4>
                  </ChurchItem>
                ))}
              </div>
            </>
          )}
        </Container>
      </FixedHeaderContent>
    </FixedHeaderContainer>
  );
}
